import { Text, makeStyles, tokens } from '@fluentui/react-components';
import { CheckmarkCircleFilled, SubtractCircleRegular } from '@fluentui/react-icons';
import type { TierFeatures } from '@worklogix/shared';

const useStyles = makeStyles({
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  on: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: '16px',
    flexShrink: 0,
  },
  off: {
    color: tokens.colorNeutralForeground4,
    fontSize: '16px',
    flexShrink: 0,
  },
  offLabel: {
    color: tokens.colorNeutralForeground3,
  },
});

/** Human labels for each entitlement flag, in stable display order. */
const FEATURE_ROWS: ReadonlyArray<{ key: keyof TierFeatures; label: string }> = [
  { key: 'reporting', label: 'Reporting' },
  { key: 'integrations', label: 'Integrations' },
  { key: 'apiAccess', label: 'API access' },
];

export interface EntitlementListProps {
  /** The tier's feature flags to render as an on/off list. */
  features: TierFeatures;
}

/**
 * Read-only list of a tier's entitlements (reporting / integrations / API
 * access) with an on/off indicator per row. "Off" uses a neutral (not error)
 * treatment — these are catalog capabilities, not failures.
 */
export function EntitlementList({ features }: EntitlementListProps): JSX.Element {
  const styles = useStyles();

  return (
    <ul className={styles.list}>
      {FEATURE_ROWS.map(({ key, label }) => {
        const enabled = features[key];
        return (
          <li key={key} className={styles.item}>
            {enabled ? (
              <CheckmarkCircleFilled className={styles.on} aria-hidden />
            ) : (
              <SubtractCircleRegular className={styles.off} aria-hidden />
            )}
            <Text className={enabled ? undefined : styles.offLabel}>
              {label}
              <span className={styles.offLabel}>{enabled ? '' : ' — not included'}</span>
            </Text>
          </li>
        );
      })}
    </ul>
  );
}
